// src/hooks.ts
import { useAsyncList, useListData } from "@react-stately/data";

// src/theme.ts
import merge from "deepmerge";
var extendTheme = (baseTheme, extendTheme2) => merge(baseTheme, extendTheme2);

// src/Aside/Aside.tsx
import React from "react";

// src/Box.ts
import { Box } from "@marigold/system";

// src/Aside/Aside.tsx
var SIDE_MAP = {
  left: [":not(style):first-of-type", ":last-child"],
  right: [":last-child", ":not(style):first-of-type"]
};
var Aside = ({
  children,
  sideWidth,
  space = "none",
  side = "left",
  stretch = true,
  wrap = "50%"
}) => {
  const [aside, content] = SIDE_MAP[side];
  return /* @__PURE__ */ React.createElement(Box, {
    css: {
      display: "flex",
      flexWrap: "wrap",
      gap: space,
      alignItems: stretch ? void 0 : "flex-start",
      [`> ${aside}`]: {
        flexBasis: sideWidth,
        flexGrow: 1
      },
      [`> ${content}`]: {
        flexBasis: 0,
        flexGrow: 999,
        minInlineSize: wrap
      }
    }
  }, children);
};

// src/Aspect/Aspect.tsx
import React2 from "react";
import { aspect } from "@marigold/tokens";
var Aspect = ({
  ratio = "square",
  maxWidth,
  children
}) => /* @__PURE__ */ React2.createElement(Box, {
  css: {
    aspectRatio: aspect[ratio],
    overflow: "hidden",
    maxWidth
  }
}, children);

// src/Badge/Badge.tsx
import React3 from "react";
import { useComponentStyles } from "@marigold/system";
var Badge = ({ variant, size, children, ...props }) => {
  const styles = useComponentStyles("Badge", { variant, size });
  return /* @__PURE__ */ React3.createElement(Box, {
    ...props,
    css: styles
  }, children);
};

// src/Breakout/Breakout.tsx
import React4 from "react";
var useAlignment = (direction) => {
  switch (direction) {
    case "right":
      return "flex-end";
    case "bottom":
      return "flex-end";
    case "center":
      return "center";
  }
  return "flex-start";
};
var Breakout = ({
  alignX,
  alignY,
  height,
  children,
  ...props
}) => {
  const alignItems = useAlignment(alignY);
  const justifyContent = useAlignment(alignX);
  return /* @__PURE__ */ React4.createElement(Box, {
    alignItems,
    justifyContent,
    width: "100%",
    height,
    display: alignY || alignX ? "flex" : "block",
    __baseCSS: {
      gridColumn: "1 / -1 !important"
    },
    ...props
  }, children);
};

// src/Body/Body.tsx
import React5 from "react";
import {
  Box as Box2,
  useComponentStyles as useComponentStyles2
} from "@marigold/system";
var Body = ({ children, variant, size, ...props }) => {
  const styles = useComponentStyles2("Body", { variant, size });
  return /* @__PURE__ */ React5.createElement(Box2, {
    as: "section",
    ...props,
    css: styles
  }, children);
};

// src/Button/Button.tsx
import React6, { forwardRef } from "react";
import { useButton } from "@react-aria/button";
import { useFocusRing } from "@react-aria/focus";
import { useHover } from "@react-aria/interactions";
import { mergeProps, useObjectRef } from "@react-aria/utils";
import {
  Box as Box3,
  useComponentStyles as useComponentStyles3,
  useStateProps
} from "@marigold/system";
var Button = forwardRef(
  ({
    as = "button",
    children,
    variant,
    size,
    disabled,
    onClick,
    onPress,
    onPressStart,
    onPressEnd,
    onPressChange,
    onPressUp,
    fullWidth,
    ...props
  }, ref) => {
    const buttonRef = useObjectRef(ref);
    const { hoverProps, isHovered } = useHover({ isDisabled: disabled });
    const { isFocusVisible, focusProps } = useFocusRing({
      autoFocus: props.autoFocus
    });
    const { buttonProps, isPressed } = useButton(
      {
        ...props,
        onClick,
        onPress,
        onPressStart,
        onPressEnd,
        onPressChange,
        onPressUp,
        elementType: typeof as === "string" ? as : "span",
        isDisabled: disabled
      },
      buttonRef
    );
    const styles = useComponentStyles3("Button", { variant, size });
    const stateProps = useStateProps({
      active: isPressed,
      focusVisible: isFocusVisible,
      hover: isHovered
    });
    return /* @__PURE__ */ React6.createElement(Box3, {
      ...mergeProps(buttonProps, focusProps, hoverProps, props),
      ...stateProps,
      as,
      ref: buttonRef,
      __baseCSS: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5ch",
        cursor: disabled ? "not-allowed" : "pointer",
        width: fullWidth ? "100%" : void 0,
        "&:focus": {
          outline: 0
        }
      },
      css: styles
    }, children);
  }
);

// src/Card/Card.tsx
import React7 from "react";
import {
  Box as Box4,
  useComponentStyles as useComponentStyles4
} from "@marigold/system";
var Card = ({
  children,
  variant,
  size,
  p,
  px,
  py,
  pt,
  pb,
  pl,
  pr,
  ...props
}) => {
  const styles = useComponentStyles4("Card", { variant, size });
  return /* @__PURE__ */ React7.createElement(Box4, {
    ...props,
    css: [styles, { p, px, py, pt, pb, pl, pr }]
  }, children);
};

// src/Center/Center.tsx
import React8 from "react";
var Center = ({
  maxWidth,
  space = "none",
  children,
  ...props
}) => /* @__PURE__ */ React8.createElement(Box, {
  css: {
    boxSizing: "content-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginInline: "auto",
    maxInlineSize: maxWidth,
    gap: space
  },
  ...props
}, children);

// src/Checkbox/Checkbox.tsx
import React11, { forwardRef as forwardRef2 } from "react";
import { useCheckbox, useCheckboxGroupItem } from "@react-aria/checkbox";
import { useFocusRing as useFocusRing2 } from "@react-aria/focus";
import { useHover as useHover2 } from "@react-aria/interactions";
import { useObjectRef as useObjectRef2 } from "@react-aria/utils";
import { useToggleState } from "@react-stately/toggle";
import {
  Box as Box7,
  useComponentStyles as useComponentStyles7,
  useStateProps as useStateProps2
} from "@marigold/system";

// src/Checkbox/CheckboxGroup.tsx
import React10, { createContext, useContext } from "react";
import { useCheckboxGroup } from "@react-aria/checkbox";
import {
  useCheckboxGroupState
} from "@react-stately/checkbox";
import {
  Box as Box6,
  useComponentStyles as useComponentStyles6
} from "@marigold/system";

// src/Label/Label.tsx
import React9 from "react";
import { Required } from "@marigold/icons";
import { Box as Box5, useComponentStyles as useComponentStyles5 } from "@marigold/system";
var Label = ({
  as = "label",
  required,
  children,
  variant,
  size,
  ...props
}) => {
  const styles = useComponentStyles5("Label", { size, variant });
  return /* @__PURE__ */ React9.createElement(Box5, {
    ...props,
    as,
    "aria-required": required,
    __baseCSS: { display: "flex", alignItems: "center", gap: 4 },
    css: styles
  }, children, required && /* @__PURE__ */ React9.createElement(Required, {
    role: "presentation",
    size: 16,
    fill: "error"
  }));
};

// src/Checkbox/CheckboxGroup.tsx
var CheckboxGroupContext = createContext(
  null
);
var useCheckboxGroupContext = () => useContext(CheckboxGroupContext);
var CheckboxGroup = ({
  children,
  variant,
  size,
  required,
  disabled,
  readOnly,
  error,
  ...rest
}) => {
  const props = {
    isRequired: required,
    isDisabled: disabled,
    isReadOnly: readOnly,
    validationState: error ? "invalid" : "valid",
    ...rest
  };
  const state = useCheckboxGroupState(props);
  const { groupProps, labelProps } = useCheckboxGroup(props, state);
  const styles = useComponentStyles6(
    "CheckboxGroup",
    { variant, size },
    { parts: ["container", "group"] }
  );
  return /* @__PURE__ */ React10.createElement(Box6, {
    ...groupProps,
    css: styles.container
  }, props.label && /* @__PURE__ */ React10.createElement(Label, {
    as: "span",
    required,
    ...labelProps
  }, props.label), /* @__PURE__ */ React10.createElement(Box6, {
    role: "presentation",
    __baseCSS: {
      display: "flex",
      flexDirection: "column",
      alignItems: "start"
    },
    css: styles.group
  }, /* @__PURE__ */ React10.createElement(CheckboxGroupContext.Provider, {
    value: { variant, size, error, ...state }
  }, children)));
};

// src/Checkbox/Checkbox.tsx
var CheckMark = () => /* @__PURE__ */ React11.createElement("svg", {
  viewBox: "0 0 12 10"
}, /* @__PURE__ */ React11.createElement("path", {
  fill: "currentColor",
  stroke: "none",
  d: "M11.915 1.548 10.367 0 4.045 6.315 1.557 3.827 0 5.373l4.045 4.046 7.87-7.871Z"
}));
var IndeterminateMark = () => /* @__PURE__ */ React11.createElement("svg", {
  width: "12",
  height: "3",
  viewBox: "0 0 12 3"
}, /* @__PURE__ */ React11.createElement("path", {
  fill: "currentColor",
  stroke: "none",
  d: "M11.5 2.04018H0.5V0.46875H11.5V2.04018Z"
}));
var Icon = ({ css, checked, indeterminate, ...props }) => /* @__PURE__ */ React11.createElement(Box7, {
  "aria-hidden": "true",
  __baseCSS: {
    flex: "0 0 16px",
    width: 16,
    height: 16,
    bg: "#fff",
    border: "1px solid #000",
    borderRadius: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 1
  },
  css,
  ...props
}, indeterminate ? /* @__PURE__ */ React11.createElement(IndeterminateMark, null) : checked ? /* @__PURE__ */ React11.createElement(CheckMark, null) : null);
var Checkbox = forwardRef2(
  ({
    size,
    variant,
    disabled,
    checked,
    defaultChecked,
    indeterminate,
    readOnly,
    required,
    error,
    ...props
  }, ref) => {
    const inputRef = useObjectRef2(ref);
    const checkboxProps = {
      isIndeterminate: indeterminate,
      isDisabled: disabled,
      isReadOnly: readOnly,
      isRequired: required,
      validationState: error ? "invalid" : "valid"
    };
    const groupState = useCheckboxGroupContext();
    const { inputProps } = groupState ? useCheckboxGroupItem(
      {
        ...props,
        ...checkboxProps,
        value: props.value
      },
      groupState,
      inputRef
    ) : useCheckbox(
      {
        isSelected: checked,
        defaultSelected: defaultChecked,
        ...checkboxProps,
        ...props
      },
      useToggleState({
        isSelected: checked,
        defaultSelected: defaultChecked,
        isReadOnly: readOnly,
        ...props
      }),
      inputRef
    );
    const styles = useComponentStyles7(
      "Checkbox",
      {
        variant: (groupState == null ? void 0 : groupState.variant) || variant,
        size: (groupState == null ? void 0 : groupState.size) || size
      },
      { parts: ["container", "label", "checkbox"] }
    );
    const { hoverProps, isHovered } = useHover2({});
    const { isFocusVisible, focusProps } = useFocusRing2();
    const stateProps = useStateProps2({
      hover: isHovered,
      focus: isFocusVisible,
      checked: inputProps.checked,
      disabled: inputProps.disabled,
      error: (groupState == null ? void 0 : groupState.error) || error,
      readOnly,
      indeterminate
    });
    return /* @__PURE__ */ React11.createElement(Box7, {
      as: "label",
      __baseCSS: {
        display: "flex",
        alignItems: "center",
        gap: "1ch",
        position: "relative"
      },
      css: styles.container,
      ...hoverProps,
      ...stateProps
    }, /* @__PURE__ */ React11.createElement(Box7, {
      as: "input",
      ref: inputRef,
      css: {
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        zIndex: 1,
        opacity: 1e-4,
        cursor: inputProps.disabled ? "not-allowed" : "pointer"
      },
      ...inputProps,
      ...focusProps
    }), /* @__PURE__ */ React11.createElement(Icon, {
      checked: inputProps.checked,
      indeterminate,
      css: styles.checkbox,
      ...stateProps
    }), props.children && /* @__PURE__ */ React11.createElement(Box7, {
      css: styles.label,
      ...stateProps
    }, props.children));
  }
);

// src/Columns/Columns.tsx
import React12, {
  Children,
  cloneElement,
  isValidElement
} from "react";
var Columns = ({
  space = "none",
  columns,
  collapseAt = "0em",
  stretch,
  children,
  ...props
}) => {
  if (Children.count(children) !== columns.length) {
    throw new Error(
      `Columns: expected ${columns.length} children, got ${Children.count(
        children
      )}`
    );
  }
  return /* @__PURE__ */ React12.createElement(Box, {
    css: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "stretch",
      height: stretch ? "100%" : void 0,
      gap: space,
      "> *": {
        flexBasis: `calc(( ${collapseAt} - 100%) * 999)`
      }
    },
    ...props
  }, Children.map(children, (child, idx) => /* @__PURE__ */ React12.createElement(Box, {
    css: {
      flexGrow: columns[idx]
    }
  }, isValidElement(child) ? cloneElement(child) : null)));
};

// src/Container/Container.tsx
import React13 from "react";
import { size as tokenSize } from "@marigold/tokens";
var ALIGN_ITEMS = {
  left: "start",
  center: "center",
  right: "end",
  none: "initial"
};
var ALIGN = {
  left: (maxWidth) => ({
    gridTemplateColumns: `minmax(0, ${maxWidth}) 1fr 1fr`,
    gridColumn: 1
  }),
  center: (maxWidth) => ({
    gridTemplateColumns: `1fr minmax(0, ${maxWidth}) 1fr`,
    gridColumn: 2
  }),
  right: (maxWidth) => ({
    gridTemplateColumns: `1fr 1fr minmax(0, ${maxWidth})`,
    gridColumn: 3
  })
};
var Container = ({
  contentType = "content",
  size = "medium",
  align = "left",
  alignItems = "none",
  children,
  ...props
}) => {
  const maxWidth = tokenSize[contentType][size];
  return /* @__PURE__ */ React13.createElement(Box, {
    display: "grid",
    css: {
      gridTemplateColumns: ALIGN[align](maxWidth).gridTemplateColumns,
      placeItems: ALIGN_ITEMS[alignItems],
      "> *": {
        gridColumn: ALIGN[align](maxWidth).gridColumn
      }
    },
    ...props
  }, children);
};

// src/Dialog/Dialog.tsx
import React21, { useRef as useRef3 } from "react";
import { useButton as useButton2 } from "@react-aria/button";
import { useDialog } from "@react-aria/dialog";
import {
  Box as Box10,
  useComponentStyles as useComponentStyles11
} from "@marigold/system";

// src/Header/Header.tsx
import React14 from "react";
import {
  useComponentStyles as useComponentStyles8
} from "@marigold/system";
var Header = ({ children, variant, size, ...props }) => {
  const styles = useComponentStyles8("Header", { variant, size });
  return /* @__PURE__ */ React14.createElement(Box, {
    as: "header",
    ...props,
    css: styles
  }, children);
};

// src/Headline/Headline.tsx
import React15 from "react";
import {
  Box as Box8,
  useComponentStyles as useComponentStyles9
} from "@marigold/system";
var Headline = ({
  children,
  variant,
  size,
  align,
  color,
  level = "1",
  ...props
}) => {
  const styles = useComponentStyles9("Headline", {
    variant,
    size: size ?? `level-${level}`
  });
  return /* @__PURE__ */ React15.createElement(Box8, {
    as: `h${level}`,
    ...props,
    css: [styles, { color, textAlign: align }]
  }, children);
};

// src/Dialog/Context.ts
import { createContext as createContext2, useContext as useContext2 } from "react";
var DialogContext = createContext2({});
var useDialogContext = () => useContext2(DialogContext);

// src/Dialog/DialogTrigger.tsx
import React20, { useRef as useRef2 } from "react";
import { PressResponder } from "@react-aria/interactions";
import { useOverlayTriggerState } from "@react-stately/overlays";

// src/Overlay/Modal.tsx
import React17, { forwardRef as forwardRef3 } from "react";
import { FocusScope } from "@react-aria/focus";
import { useModal, useOverlay, usePreventScroll } from "@react-aria/overlays";
import { mergeProps as mergeProps2, useObjectRef as useObjectRef3 } from "@react-aria/utils";

// src/Overlay/Underlay.tsx
import React16 from "react";
import { Box as Box9, useComponentStyles as useComponentStyles10 } from "@marigold/system";
var Underlay = ({ size, variant, ...props }) => {
  const styles = useComponentStyles10("Underlay", { size, variant });
  return /* @__PURE__ */ React16.createElement(Box9, {
    __baseCSS: {
      position: "fixed",
      inset: 0,
      zIndex: 1
    },
    css: styles,
    ...props
  });
};

// src/Overlay/Modal.tsx
var Modal = forwardRef3(
  ({ children, open, dismissable, keyboardDismissable, onClose, ...props }, ref) => {
    const modalRef = useObjectRef3(ref);
    const { overlayProps, underlayProps } = useOverlay(
      {
        isOpen: open,
        onClose,
        isDismissable: dismissable,
        isKeyboardDismissDisabled: !keyboardDismissable
      },
      modalRef
    );
    usePreventScroll();
    const { modalProps } = useModal({});
    return /* @__PURE__ */ React17.createElement(FocusScope, {
      contain: true,
      restoreFocus: true,
      autoFocus: true
    }, /* @__PURE__ */ React17.createElement(Underlay, {
      ...underlayProps
    }), /* @__PURE__ */ React17.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "fixed",
        inset: 0,
        zIndex: 2,
        pointerEvents: "none"
      },
      ref: modalRef,
      ...mergeProps2(props, overlayProps, modalProps)
    }, /* @__PURE__ */ React17.createElement("div", {
      style: { pointerEvents: "auto" }
    }, children)));
  }
);

// src/Overlay/Overlay.tsx
import React18 from "react";
import { OverlayContainer } from "@react-aria/overlays";
var Overlay = ({
  children,
  open = false,
  container,
  ...props
}) => {
  if (!open) {
    return null;
  }
  return /* @__PURE__ */ React18.createElement(OverlayContainer, {
    portalContainer: container
  }, /* @__PURE__ */ React18.createElement(Box, {
    ...props
  }, children));
};

// src/Overlay/Popover.tsx
import React19, { forwardRef as forwardRef4, useRef } from "react";
import { useModal as useModal2, useOverlay as useOverlay2 } from "@react-aria/overlays";
import { mergeProps as mergeProps3 } from "@react-aria/utils";
var Popover = forwardRef4(
  ({
    children,
    open,
    dismissable,
    keyboardDismissDisabled,
    shouldCloseOnBlur,
    minWidth = 0,
    ...props
  }, ref) => {
    const fallbackRef = useRef(null);
    const popoverRef = ref || fallbackRef;
    const { overlayProps } = useOverlay2(
      {
        isOpen: open,
        isDismissable: dismissable,
        isKeyboardDismissDisabled: keyboardDismissDisabled,
        shouldCloseOnBlur,
        ...props
      },
      popoverRef
    );
    const { modalProps } = useModal2({});
    const style = { minWidth };
    return /* @__PURE__ */ React19.createElement(Overlay, {
      open
    }, /* @__PURE__ */ React19.createElement(Box, {
      ref: popoverRef,
      role: "presentation",
      ...mergeProps3(props, overlayProps, modalProps, style)
    }, children));
  }
);

// src/Dialog/DialogTrigger.tsx
var DialogTrigger = ({
  children,
  dismissable = true,
  keyboardDismissable = true
}) => {
  const [dialogTrigger, dialog] = React20.Children.toArray(children);
  const dialogTriggerRef = useRef2(null);
  const state = useOverlayTriggerState({});
  const ctx = { open: state.isOpen, close: state.close };
  return /* @__PURE__ */ React20.createElement(DialogContext.Provider, {
    value: ctx
  }, /* @__PURE__ */ React20.createElement(PressResponder, {
    ref: dialogTriggerRef,
    isPressed: state.isOpen,
    onPress: state.toggle
  }, dialogTrigger), /* @__PURE__ */ React20.createElement(Overlay, {
    open: state.isOpen
  }, /* @__PURE__ */ React20.createElement(Modal, {
    open: state.isOpen,
    onClose: state.close,
    dismissable,
    keyboardDismissable
  }, dialog)));
};

// src/Dialog/Dialog.tsx
var CloseButton = ({ css }) => {
  const ref = useRef3(null);
  const { close } = useDialogContext();
  const { buttonProps } = useButton2(
    {
      onPress: () => close == null ? void 0 : close()
    },
    ref
  );
  return /* @__PURE__ */ React21.createElement(Box10, {
    css: { display: "flex", justifyContent: "flex-end" }
  }, /* @__PURE__ */ React21.createElement(Box10, {
    as: "button",
    __baseCSS: {
      outline: "none",
      border: "none",
      cursor: "pointer",
      height: 16,
      width: 16,
      lineHeight: 1,
      p: 0
    },
    css,
    ref,
    ...buttonProps
  }, /* @__PURE__ */ React21.createElement("svg", {
    viewBox: "0 0 20 20",
    fill: "currentColor"
  }, /* @__PURE__ */ React21.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
  }))));
};
var addTitleProps = (children, titleProps) => {
  const childs = React21.Children.toArray(children);
  const titleIndex = childs.findIndex(
    (child) => React21.isValidElement(child) && (child.type === Header || child.type === Headline)
  );
  if (titleIndex < 0) {
    console.warn(
      "No child in <Dialog> found that can act as title for accessibility. Please add a <Header> or <Headline> as direct child."
    );
    return children;
  }
  const titleChild = React21.cloneElement(
    childs[titleIndex],
    titleProps
  );
  childs.splice(titleIndex, 1, titleChild);
  return childs;
};
var Dialog = ({
  children,
  variant,
  size,
  closeButton,
  ...props
}) => {
  const ref = useRef3(null);
  const { close } = useDialogContext();
  const { dialogProps, titleProps } = useDialog(props, ref);
  const styles = useComponentStyles11(
    "Dialog",
    { variant, size },
    { parts: ["container", "closeButton"] }
  );
  return /* @__PURE__ */ React21.createElement(Box10, {
    __baseCSS: { bg: "#fff" },
    css: styles.container,
    ...dialogProps
  }, closeButton && /* @__PURE__ */ React21.createElement(CloseButton, {
    css: styles.closeButton
  }), typeof children === "function" ? children({ close, titleProps }) : props["aria-labelledby"] ? children : addTitleProps(children, titleProps));
};
Dialog.Trigger = DialogTrigger;

// src/Divider/Divider.tsx
import React22 from "react";
import { useSeparator } from "@react-aria/separator";
import { Box as Box11, useComponentStyles as useComponentStyles12 } from "@marigold/system";
var Divider = ({ variant, ...props }) => {
  const { separatorProps } = useSeparator(props);
  const styles = useComponentStyles12("Divider", { variant });
  return /* @__PURE__ */ React22.createElement(Box11, {
    css: styles,
    ...props,
    ...separatorProps
  });
};

// src/Footer/Footer.tsx
import React23 from "react";
import {
  useComponentStyles as useComponentStyles13
} from "@marigold/system";
var Footer = ({ children, variant, size, ...props }) => {
  const styles = useComponentStyles13("Footer", { variant, size });
  return /* @__PURE__ */ React23.createElement(Box, {
    as: "footer",
    ...props,
    css: styles
  }, children);
};

// src/Image/Image.tsx
import React24 from "react";
import { Box as Box12 } from "@marigold/system";
import { useComponentStyles as useComponentStyles14 } from "@marigold/system";
var Image = ({
  variant,
  size,
  fit,
  position,
  ...props
}) => {
  const styles = useComponentStyles14("Image", { variant, size });
  const css = {
    ...styles,
    objectFit: fit,
    objectPosition: position
  };
  return /* @__PURE__ */ React24.createElement(Box12, {
    ...props,
    as: "img",
    __baseCSS: fit ? { width: " 100%", height: "100%" } : {},
    css
  });
};

// src/Inline/Inline.tsx
import React25 from "react";
var ALIGNMENT_X = {
  left: "flex-start",
  center: "center",
  right: "flex-end"
};
var ALIGNMENT_Y = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end"
};
var Inline = ({
  space = "none",
  alignX = "left",
  alignY = "center",
  children,
  ...props
}) => /* @__PURE__ */ React25.createElement(Box, {
  css: {
    display: "flex",
    flexWrap: "wrap",
    gap: space,
    alignItems: ALIGNMENT_Y[alignY],
    justifyContent: ALIGNMENT_X[alignX]
  },
  ...props
}, children);

// src/Input/Input.tsx
import React26, { forwardRef as forwardRef5 } from "react";
import { Box as Box13, useComponentStyles as useComponentStyles15 } from "@marigold/system";
var Input = forwardRef5(
  ({ variant, size, type = "text", ...props }, ref) => {
    const styles = useComponentStyles15("Input", { variant, size });
    return /* @__PURE__ */ React26.createElement(Box13, {
      ...props,
      ref,
      as: "input",
      type,
      css: styles
    });
  }
);

// src/Link/Link.tsx
import React27, { forwardRef as forwardRef6 } from "react";
import { useLink } from "@react-aria/link";
import { useComponentStyles as useComponentStyles16 } from "@marigold/system";
import { useObjectRef as useObjectRef4 } from "@react-aria/utils";
var Link = forwardRef6(
  ({
    as = "a",
    variant,
    size,
    children,
    disabled,
    onPress,
    onPressStart,
    ...props
  }, ref) => {
    const linkRef = useObjectRef4(ref);
    const { linkProps } = useLink(
      {
        ...props,
        elementType: typeof as === "string" ? as : "span",
        isDisabled: disabled
      },
      linkRef
    );
    const styles = useComponentStyles16("Link", { variant, size });
    return /* @__PURE__ */ React27.createElement(React27.Fragment, null, " ", /* @__PURE__ */ React27.createElement(Box, {
      as,
      role: "link",
      css: styles,
      ref: linkRef,
      ...props,
      ...linkProps
    }, children), " ");
  }
);

// src/List/List.tsx
import React29 from "react";
import {
  Box as Box15,
  useComponentStyles as useComponentStyles17
} from "@marigold/system";

// src/List/Context.ts
import { createContext as createContext3, useContext as useContext3 } from "react";
var ListContext = createContext3({});
var useListContext = () => useContext3(ListContext);

// src/List/ListItem.tsx
import React28 from "react";
import { Box as Box14 } from "@marigold/system";
var ListItem = ({ children, ...props }) => {
  const { styles } = useListContext();
  return /* @__PURE__ */ React28.createElement(Box14, {
    ...props,
    as: "li",
    css: styles
  }, children);
};

// src/List/List.tsx
var List = ({
  as = "ul",
  children,
  variant,
  size,
  ...props
}) => {
  const styles = useComponentStyles17(
    "List",
    { variant, size },
    { parts: ["ul", "ol", "item"] }
  );
  return /* @__PURE__ */ React29.createElement(Box15, {
    ...props,
    as,
    css: styles[as]
  }, /* @__PURE__ */ React29.createElement(ListContext.Provider, {
    value: { styles: styles.item }
  }, children));
};
List.Item = ListItem;

// src/Menu/Menu.tsx
import React32, { useRef as useRef6 } from "react";
import { FocusScope as FocusScope2 } from "@react-aria/focus";
import { useMenu } from "@react-aria/menu";
import { DismissButton } from "@react-aria/overlays";
import { Item } from "@react-stately/collections";
import { useTreeState } from "@react-stately/tree";
import {
  Box as Box17,
  useComponentStyles as useComponentStyles18
} from "@marigold/system";

// src/Menu/Context.ts
import { createContext as createContext4, useContext as useContext4 } from "react";
var MenuContext = createContext4({});
var useMenuContext = () => useContext4(MenuContext);

// src/Menu/MenuTrigger.tsx
import React30, { useRef as useRef4 } from "react";
import { useMenuTriggerState } from "@react-stately/menu";
import { useMenuTrigger } from "@react-aria/menu";
import { useOverlayPosition } from "@react-aria/overlays";
import { PressResponder as PressResponder2 } from "@react-aria/interactions";
var MenuTrigger = ({ disabled, children }) => {
  const [menuTrigger, menu] = React30.Children.toArray(children);
  const menuTriggerRef = useRef4(null);
  const overlayRef = useRef4(null);
  const state = useMenuTriggerState({});
  const { menuTriggerProps, menuProps } = useMenuTrigger(
    { trigger: "press", isDisabled: disabled },
    state,
    menuTriggerRef
  );
  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef: menuTriggerRef,
    overlayRef,
    isOpen: state.isOpen
  });
  const menuContext = {
    ...menuProps,
    open: state.isOpen,
    onClose: state.close,
    autoFocus: state.focusStrategy,
    triggerWidth: menuTriggerRef.current ? menuTriggerRef.current.offsetWidth : void 0
  };
  return /* @__PURE__ */ React30.createElement(MenuContext.Provider, {
    value: menuContext
  }, /* @__PURE__ */ React30.createElement(PressResponder2, {
    ...menuTriggerProps,
    ref: menuTriggerRef,
    isPressed: state.isOpen
  }, menuTrigger), /* @__PURE__ */ React30.createElement(Popover, {
    open: state.isOpen,
    onClose: state.close,
    dismissable: true,
    shouldCloseOnBlur: true,
    ref: overlayRef,
    ...positionProps
  }, menu));
};

// src/Menu/MenuItem.tsx
import React31, { useRef as useRef5 } from "react";
import { useFocusRing as useFocusRing3 } from "@react-aria/focus";
import { useMenuItem } from "@react-aria/menu";
import { mergeProps as mergeProps4 } from "@react-aria/utils";
import { Box as Box16, useStateProps as useStateProps3 } from "@marigold/system";
var MenuItem = ({ item, state, onAction, css }) => {
  const ref = useRef5(null);
  const { onClose } = useMenuContext();
  const { menuItemProps } = useMenuItem(
    {
      key: item.key,
      onAction,
      onClose
    },
    state,
    ref
  );
  const { isFocusVisible, focusProps } = useFocusRing3();
  const stateProps = useStateProps3({
    focus: isFocusVisible
  });
  return /* @__PURE__ */ React31.createElement(Box16, {
    as: "li",
    ref,
    __baseCSS: {
      "&:focus": {
        outline: 0
      }
    },
    css,
    ...mergeProps4(menuItemProps, focusProps),
    ...stateProps
  }, item.rendered);
};

// src/Menu/Menu.tsx
var Menu = ({ variant, size, ...props }) => {
  const { triggerWidth, ...menuContext } = useMenuContext();
  const ownProps = { ...props, ...menuContext };
  const ref = useRef6(null);
  const state = useTreeState({ ...ownProps, selectionMode: "none" });
  const { menuProps } = useMenu(ownProps, state, ref);
  const styles = useComponentStyles18(
    "Menu",
    { variant, size },
    { parts: ["container", "item"] }
  );
  return /* @__PURE__ */ React32.createElement(FocusScope2, {
    restoreFocus: true
  }, /* @__PURE__ */ React32.createElement("div", null, /* @__PURE__ */ React32.createElement(DismissButton, {
    onDismiss: ownProps.onClose
  }), /* @__PURE__ */ React32.createElement(Box17, {
    as: "ul",
    ref,
    style: { width: triggerWidth },
    __baseCSS: {
      listStyle: "none",
      p: 0,
      overflowWrap: "break-word"
    },
    css: styles.container,
    ...menuProps
  }, [...state.collection].map((item) => /* @__PURE__ */ React32.createElement(MenuItem, {
    key: item.key,
    item,
    state,
    onAction: props.onSelect,
    css: styles.item
  }))), /* @__PURE__ */ React32.createElement(DismissButton, {
    onDismiss: ownProps.onClose
  })));
};
Menu.Trigger = MenuTrigger;
Menu.Item = Item;

// src/Message/Message.tsx
import React33 from "react";
import { Exclamation, Info, Notification } from "@marigold/icons";
import { useComponentStyles as useComponentStyles19 } from "@marigold/system";
var Message = ({
  messageTitle,
  variant = "info",
  size,
  children,
  ...props
}) => {
  const styles = useComponentStyles19(
    "Message",
    {
      variant,
      size
    },
    { parts: ["container", "icon", "title", "content"] }
  );
  var icon = /* @__PURE__ */ React33.createElement(Info, null);
  if (variant === "warning") {
    icon = /* @__PURE__ */ React33.createElement(Notification, null);
  }
  if (variant === "error") {
    icon = /* @__PURE__ */ React33.createElement(Exclamation, null);
  }
  return /* @__PURE__ */ React33.createElement(Box, {
    css: styles.container,
    ...props
  }, /* @__PURE__ */ React33.createElement(Box, {
    __baseCSS: { display: "flex", alignItems: "center", gap: 4 }
  }, /* @__PURE__ */ React33.createElement(Box, {
    role: "presentation",
    css: styles.icon
  }, icon), /* @__PURE__ */ React33.createElement(Box, {
    css: styles.title
  }, messageTitle)), /* @__PURE__ */ React33.createElement(Box, {
    css: styles.content
  }, children));
};

// src/NumberField/NumberField.tsx
import React37, { forwardRef as forwardRef7 } from "react";
import { useFocusRing as useFocusRing4 } from "@react-aria/focus";
import { useHover as useHover4 } from "@react-aria/interactions";
import { useLocale } from "@react-aria/i18n";
import { useNumberField } from "@react-aria/numberfield";
import { mergeProps as mergeProps6, useObjectRef as useObjectRef5 } from "@react-aria/utils";
import { useNumberFieldState } from "@react-stately/numberfield";
import {
  Box as Box21,
  useComponentStyles as useComponentStyles21,
  useStateProps as useStateProps5
} from "@marigold/system";

// src/FieldBase/FieldBase.tsx
import React35 from "react";
import { Box as Box19 } from "@marigold/system";

// src/HelpText/HelpText.tsx
import React34 from "react";
import { Exclamation as Exclamation2 } from "@marigold/icons";
import {
  Box as Box18,
  useComponentStyles as useComponentStyles20
} from "@marigold/system";
var HelpText = ({
  variant,
  size,
  disabled,
  description,
  descriptionProps,
  error,
  errorMessage,
  errorMessageProps,
  ...props
}) => {
  var _a;
  const hasErrorMessage = errorMessage && error;
  const styles = useComponentStyles20(
    "HelpText",
    { variant, size },
    { parts: ["container", "icon"] }
  );
  return /* @__PURE__ */ React34.createElement(Box18, {
    ...hasErrorMessage ? errorMessageProps : descriptionProps,
    ...props,
    __baseCSS: { display: "flex", alignItems: "center", gap: 4 },
    css: styles.container
  }, hasErrorMessage ? /* @__PURE__ */ React34.createElement(React34.Fragment, null, /* @__PURE__ */ React34.createElement(Exclamation2, {
    role: "presentation",
    size: ((_a = styles == null ? void 0 : styles.icon) == null ? void 0 : _a.size) || 16
  }), errorMessage) : /* @__PURE__ */ React34.createElement(React34.Fragment, null, description));
};

// src/FieldBase/FieldBase.tsx
var FieldBase = ({
  children,
  variant,
  size,
  width = "100%",
  disabled,
  required,
  label,
  labelProps,
  description,
  descriptionProps,
  error,
  errorMessage,
  errorMessageProps,
  stateProps
}) => {
  const hasHelpText = !!description || errorMessage && error;
  return /* @__PURE__ */ React35.createElement(Box19, {
    css: { display: "flex", flexDirection: "column", width }
  }, label && /* @__PURE__ */ React35.createElement(Label, {
    required,
    variant,
    size,
    ...labelProps,
    ...stateProps
  }, label), children, hasHelpText && /* @__PURE__ */ React35.createElement(HelpText, {
    ...stateProps,
    variant,
    size,
    disabled,
    description,
    descriptionProps,
    error,
    errorMessage,
    errorMessageProps
  }));
};

// src/NumberField/StepButton.tsx
import React36, { useRef as useRef7 } from "react";
import { useButton as useButton3 } from "@react-aria/button";
import { useHover as useHover3 } from "@react-aria/interactions";
import { mergeProps as mergeProps5 } from "@react-aria/utils";
import { Box as Box20, useStateProps as useStateProps4 } from "@marigold/system";
var Plus = () => /* @__PURE__ */ React36.createElement(Box20, {
  as: "svg",
  __baseCSS: { width: 16, height: 16 },
  viewBox: "0 0 20 20",
  fill: "currentColor"
}, /* @__PURE__ */ React36.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
}));
var Minus = () => /* @__PURE__ */ React36.createElement(Box20, {
  as: "svg",
  __baseCSS: { width: 16, height: 16 },
  viewBox: "0 0 20 20",
  fill: "currentColor"
}, /* @__PURE__ */ React36.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
}));
var StepButton = ({ direction, css, ...props }) => {
  const ref = useRef7(null);
  const { buttonProps, isPressed } = useButton3(
    { ...props, elementType: "div" },
    ref
  );
  const { hoverProps, isHovered } = useHover3(props);
  const stateProps = useStateProps4({
    active: isPressed,
    hover: isHovered,
    disabled: props.isDisabled
  });
  const Icon3 = direction === "up" ? Plus : Minus;
  return /* @__PURE__ */ React36.createElement(Box20, {
    __baseCSS: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: props.isDisabled ? "not-allowed" : "pointer"
    },
    css,
    ...mergeProps5(buttonProps, hoverProps),
    ...stateProps
  }, /* @__PURE__ */ React36.createElement(Icon3, null));
};

// src/NumberField/NumberField.tsx
var NumberField = forwardRef7(
  ({
    variant,
    size,
    width,
    disabled,
    required,
    readOnly,
    error,
    hideStepper,
    ...rest
  }, ref) => {
    const props = {
      isDisabled: disabled,
      isRequired: required,
      isReadOnly: readOnly,
      validationState: error ? "invalid" : "valid",
      ...rest
    };
    const showStepper = !hideStepper;
    const { locale } = useLocale();
    const inputRef = useObjectRef5(ref);
    const state = useNumberFieldState({ ...props, locale });
    const {
      labelProps,
      groupProps,
      inputProps,
      descriptionProps,
      errorMessageProps,
      incrementButtonProps,
      decrementButtonProps
    } = useNumberField(props, state, inputRef);
    const { hoverProps, isHovered } = useHover4({ isDisabled: disabled });
    const { focusProps, isFocused } = useFocusRing4({
      within: true,
      isTextInput: true,
      autoFocus: props.autoFocus
    });
    const styles = useComponentStyles21(
      "NumberField",
      { variant, size },
      { parts: ["group", "stepper"] }
    );
    const stateProps = useStateProps5({
      hover: isHovered,
      focus: isFocused,
      disabled,
      readOnly,
      error
    });
    return /* @__PURE__ */ React37.createElement(FieldBase, {
      label: props.label,
      labelProps,
      required,
      description: props.description,
      descriptionProps,
      error,
      errorMessage: props.errorMessage,
      errorMessageProps,
      stateProps,
      variant,
      size,
      width
    }, /* @__PURE__ */ React37.createElement(Box21, {
      "data-group": true,
      __baseCSS: {
        display: "flex",
        alignItems: "stretch",
        "> input": {
          flex: 1,
          minWidth: 0
        }
      },
      css: styles.group,
      ...mergeProps6(groupProps, focusProps, hoverProps),
      ...stateProps
    }, showStepper && /* @__PURE__ */ React37.createElement(StepButton, {
      direction: "down",
      css: styles.stepper,
      ...decrementButtonProps
    }), /* @__PURE__ */ React37.createElement(Input, {
      ref: inputRef,
      variant,
      size,
      ...inputProps,
      ...stateProps
    }), showStepper && /* @__PURE__ */ React37.createElement(StepButton, {
      direction: "up",
      css: styles.stepper,
      ...incrementButtonProps
    })));
  }
);

// src/Provider/index.ts
import { useTheme as useTheme2, ThemeProvider as ThemeProvider2 } from "@marigold/system";
import { SSRProvider } from "@react-aria/ssr";

// src/Provider/MarigoldProvider.tsx
import React38 from "react";
import { OverlayProvider } from "@react-aria/overlays";
import {
  Global,
  ThemeProvider,
  useTheme,
  __defaultTheme
} from "@marigold/system";
function MarigoldProvider({
  children,
  theme,
  selector,
  normalizeDocument = true
}) {
  var _a;
  const outer = useTheme();
  const isTopLevel = outer.theme === __defaultTheme;
  if (((_a = outer.theme) == null ? void 0 : _a.root) && !isTopLevel && !selector) {
    throw new Error(
      `[MarigoldProvider] You cannot nest a MarigoldProvider inside another MarigoldProvider without a "selector"! 
      Nested themes with a "root" property must specify a "selector" to prevent accidentally overriding global CSS`
    );
  }
  return /* @__PURE__ */ React38.createElement(ThemeProvider, {
    theme
  }, /* @__PURE__ */ React38.createElement(Global, {
    normalizeDocument: isTopLevel && normalizeDocument,
    selector
  }), isTopLevel ? /* @__PURE__ */ React38.createElement(OverlayProvider, null, children) : children);
}

// src/Radio/Radio.tsx
import React40, {
  forwardRef as forwardRef8
} from "react";
import { useHover as useHover5 } from "@react-aria/interactions";
import { useFocusRing as useFocusRing5 } from "@react-aria/focus";
import { useRadio } from "@react-aria/radio";
import { useObjectRef as useObjectRef6 } from "@react-aria/utils";
import {
  Box as Box23,
  useComponentStyles as useComponentStyles23,
  useStateProps as useStateProps6
} from "@marigold/system";

// src/Radio/Context.ts
import { createContext as createContext5, useContext as useContext5 } from "react";
var RadioGroupContext = createContext5(
  null
);
var useRadioGroupContext = () => useContext5(RadioGroupContext);

// src/Radio/RadioGroup.tsx
import React39 from "react";
import { useRadioGroup } from "@react-aria/radio";
import { useRadioGroupState } from "@react-stately/radio";
import {
  Box as Box22,
  useComponentStyles as useComponentStyles22
} from "@marigold/system";
var RadioGroup = ({
  children,
  orientation = "vertical",
  size,
  variant,
  width,
  required,
  disabled,
  readOnly,
  error,
  ...rest
}) => {
  const props = {
    isRequired: required,
    isDisabled: disabled,
    isReadOnly: readOnly,
    validationState: error ? "invalid" : "valid",
    ...rest
  };
  const state = useRadioGroupState(props);
  const { radioGroupProps, labelProps } = useRadioGroup(props, state);
  const styles = useComponentStyles22(
    "RadioGroup",
    { variant, size },
    { parts: ["container", "group"] }
  );
  return /* @__PURE__ */ React39.createElement(Box22, {
    ...radioGroupProps,
    css: styles.container
  }, props.label && /* @__PURE__ */ React39.createElement(Label, {
    as: "span",
    required,
    ...labelProps
  }, props.label), /* @__PURE__ */ React39.createElement(Box22, {
    role: "presentation",
    "data-orientation": orientation,
    __baseCSS: {
      display: "flex",
      flexDirection: orientation === "vertical" ? "column" : "row",
      alignItems: "start",
      gap: orientation === "vertical" ? "0.5ch" : "1.5ch"
    },
    css: styles.group
  }, /* @__PURE__ */ React39.createElement(RadioGroupContext.Provider, {
    value: { variant, size, width, error, ...state }
  }, children)));
};

// src/Radio/Radio.tsx
var Dot = () => /* @__PURE__ */ React40.createElement("svg", {
  viewBox: "0 0 6 6"
}, /* @__PURE__ */ React40.createElement("circle", {
  fill: "currentColor",
  cx: "3",
  cy: "3",
  r: "3"
}));
var Icon2 = ({ checked, css, ...props }) => /* @__PURE__ */ React40.createElement(Box23, {
  "aria-hidden": "true",
  __baseCSS: {
    width: 16,
    height: 16,
    bg: "#fff",
    border: "1px solid #000",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    p: 4
  },
  css,
  ...props
}, checked ? /* @__PURE__ */ React40.createElement(Dot, null) : null);
var Radio = forwardRef8(
  ({ width, disabled, ...props }, ref) => {
    const {
      variant,
      size,
      error,
      width: groupWidth,
      ...state
    } = useRadioGroupContext();
    const inputRef = useObjectRef6(ref);
    const { inputProps } = useRadio(
      { isDisabled: disabled, ...props },
      state,
      inputRef
    );
    const styles = useComponentStyles23(
      "Radio",
      { variant: variant || props.variant, size: size || props.size },
      { parts: ["container", "label", "radio"] }
    );
    const { hoverProps, isHovered } = useHover5({});
    const { isFocusVisible, focusProps } = useFocusRing5();
    const stateProps = useStateProps6({
      hover: isHovered,
      focus: isFocusVisible,
      checked: inputProps.checked,
      disabled: inputProps.disabled,
      readOnly: props.readOnly,
      error
    });
    return /* @__PURE__ */ React40.createElement(Box23, {
      as: "label",
      __baseCSS: {
        display: "flex",
        alignItems: "center",
        gap: "1ch",
        position: "relative",
        width: width || groupWidth || "100%"
      },
      css: styles.container,
      ...hoverProps,
      ...stateProps
    }, /* @__PURE__ */ React40.createElement(Box23, {
      as: "input",
      ref: inputRef,
      css: {
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        zIndex: 1,
        opacity: 1e-4,
        cursor: inputProps.disabled ? "not-allowed" : "pointer"
      },
      ...inputProps,
      ...focusProps
    }), /* @__PURE__ */ React40.createElement(Icon2, {
      checked: inputProps.checked,
      css: styles.radio,
      ...stateProps
    }), /* @__PURE__ */ React40.createElement(Box23, {
      css: styles.label,
      ...stateProps
    }, props.children));
  }
);
Radio.Group = RadioGroup;

// src/Select/Select.tsx
import React44, {
  forwardRef as forwardRef10,
  useRef as useRef9
} from "react";
import { useButton as useButton4 } from "@react-aria/button";
import { FocusScope as FocusScope3, useFocusRing as useFocusRing6 } from "@react-aria/focus";
import { useLocalizedStringFormatter } from "@react-aria/i18n";
import { DismissButton as DismissButton2, useOverlayPosition as useOverlayPosition2 } from "@react-aria/overlays";
import { HiddenSelect, useSelect } from "@react-aria/select";
import { useSelectState } from "@react-stately/select";
import { Item as Item2, Section } from "@react-stately/collections";
import { mergeProps as mergeProps7, useObjectRef as useObjectRef8 } from "@react-aria/utils";
import {
  Box as Box27,
  useComponentStyles as useComponentStyles25,
  useStateProps as useStateProps8
} from "@marigold/system";

// src/ListBox/ListBox.tsx
import React43, { forwardRef as forwardRef9 } from "react";
import { useObjectRef as useObjectRef7 } from "@react-aria/utils";
import {
  Box as Box26,
  useComponentStyles as useComponentStyles24
} from "@marigold/system";
import { useListBox } from "@react-aria/listbox";

// src/ListBox/Context.ts
import { createContext as createContext6, useContext as useContext6 } from "react";
var ListBoxContext = createContext6({});
var useListBoxContext = () => useContext6(ListBoxContext);

// src/ListBox/ListBoxSection.tsx
import React42 from "react";
import { useListBoxSection } from "@react-aria/listbox";
import { Box as Box25 } from "@marigold/system";

// src/ListBox/ListBoxOption.tsx
import React41, { useRef as useRef8 } from "react";
import { useOption } from "@react-aria/listbox";
import { Box as Box24, useStateProps as useStateProps7 } from "@marigold/system";
var ListBoxOption = ({ item, state }) => {
  const ref = useRef8(null);
  const { optionProps, isSelected, isDisabled, isFocused } = useOption(
    {
      key: item.key
    },
    state,
    ref
  );
  const { styles } = useListBoxContext();
  const stateProps = useStateProps7({
    selected: isSelected,
    disabled: isDisabled,
    focusVisible: isFocused
  });
  return /* @__PURE__ */ React41.createElement(Box24, {
    as: "li",
    ref,
    css: styles.option,
    ...optionProps,
    ...stateProps
  }, item.rendered);
};

// src/ListBox/ListBoxSection.tsx
var ListBoxSection = ({ section, state }) => {
  const { itemProps, headingProps, groupProps } = useListBoxSection({
    heading: section.rendered,
    "aria-label": section["aria-label"]
  });
  const { styles } = useListBoxContext();
  return /* @__PURE__ */ React42.createElement(Box25, {
    as: "li",
    css: styles.section,
    ...itemProps
  }, section.rendered && /* @__PURE__ */ React42.createElement(Box25, {
    css: styles.sectionTitle,
    ...headingProps
  }, section.rendered), /* @__PURE__ */ React42.createElement(Box25, {
    as: "ul",
    __baseCSS: { listStyle: "none", p: 0 },
    css: styles.list,
    ...groupProps
  }, [...section.childNodes].map((node) => /* @__PURE__ */ React42.createElement(ListBoxOption, {
    key: node.key,
    item: node,
    state
  }))));
};

// src/ListBox/ListBox.tsx
var ListBox = forwardRef9(
  ({ state, variant, size, ...props }, ref) => {
    const innerRef = useObjectRef7(ref);
    const { listBoxProps } = useListBox(props, state, innerRef);
    const styles = useComponentStyles24(
      "ListBox",
      { variant, size },
      { parts: ["container", "list", "option", "section", "sectionTitle"] }
    );
    return /* @__PURE__ */ React43.createElement(ListBoxContext.Provider, {
      value: { styles }
    }, /* @__PURE__ */ React43.createElement(Box26, {
      css: styles.container
    }, /* @__PURE__ */ React43.createElement(Box26, {
      as: "ul",
      ref: innerRef,
      __baseCSS: { listStyle: "none", p: 0 },
      css: styles.list,
      ...listBoxProps
    }, [...state.collection].map(
      (item) => item.type === "section" ? /* @__PURE__ */ React43.createElement(ListBoxSection, {
        key: item.key,
        section: item,
        state
      }) : /* @__PURE__ */ React43.createElement(ListBoxOption, {
        key: item.key,
        item,
        state
      })
    ))));
  }
);

// src/Select/intl.ts
var messages = {
  "en-US": {
    placeholder: "Select an option\u2026"
  },
  "de-DE": {
    placeholder: "Option ausw\xE4hlen\u2026"
  }
};

// src/Select/Select.tsx
var Chevron = ({ css }) => /* @__PURE__ */ React44.createElement(Box27, {
  as: "svg",
  __baseCSS: { width: 16, height: 16, fill: "none" },
  css,
  viewBox: "0 0 24 24",
  stroke: "currentColor",
  strokeWidth: 2
}, /* @__PURE__ */ React44.createElement("path", {
  strokeLinecap: "round",
  strokeLinejoin: "round",
  d: "M19 9l-7 7-7-7"
}));
var Select = forwardRef10(
  ({ variant, size, width, open, disabled, required, error, ...rest }, ref) => {
    const formatMessage = useLocalizedStringFormatter(messages);
    const buttonRef = useObjectRef8(ref);
    const props = {
      isOpen: open,
      isDisabled: disabled,
      isRequired: required,
      validationState: error ? "invalid" : "valid",
      placeholder: rest.placeholder || formatMessage.format("placeholder"),
      ...rest
    };
    const state = useSelectState(props);
    const {
      labelProps,
      triggerProps,
      valueProps,
      menuProps,
      descriptionProps,
      errorMessageProps
    } = useSelect(props, state, buttonRef);
    const { buttonProps } = useButton4(
      { isDisabled: disabled, ...triggerProps },
      buttonRef
    );
    const { focusProps, isFocusVisible } = useFocusRing6();
    const overlayRef = useRef9(null);
    const { overlayProps: positionProps } = useOverlayPosition2({
      targetRef: buttonRef,
      overlayRef,
      isOpen: state.isOpen,
      placement: "bottom left"
    });
    const styles = useComponentStyles25(
      "Select",
      { variant, size },
      { parts: ["container", "button", "icon"] }
    );
    const stateProps = useStateProps8({
      disabled,
      error,
      focusVisible: isFocusVisible,
      expanded: state.isOpen
    });
    return /* @__PURE__ */ React44.createElement(FieldBase, {
      variant,
      size,
      width,
      label: props.label,
      labelProps: { as: "span", ...labelProps },
      description: props.description,
      descriptionProps,
      error,
      errorMessage: props.errorMessage,
      errorMessageProps,
      stateProps,
      disabled,
      required
    }, /* @__PURE__ */ React44.createElement(HiddenSelect, {
      state,
      triggerRef: buttonRef,
      label: props.label,
      name: props.name,
      isDisabled: disabled
    }), /* @__PURE__ */ React44.createElement(Box27, {
      as: "button",
      __baseCSS: {
        display: "flex",
        position: "relative",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%"
      },
      css: styles.button,
      ref: buttonRef,
      ...mergeProps7(buttonProps, focusProps),
      ...stateProps
    }, /* @__PURE__ */ React44.createElement(Box27, {
      css: {
        overflow: "hidden",
        whiteSpace: "nowrap"
      },
      ...valueProps
    }, state.selectedItem ? state.selectedItem.rendered : props.placeholder), /* @__PURE__ */ React44.createElement(Chevron, {
      css: styles.icon
    })), /* @__PURE__ */ React44.createElement(Popover, {
      open: state.isOpen,
      onClose: state.close,
      dismissable: true,
      shouldCloseOnBlur: true,
      minWidth: buttonRef.current ? buttonRef.current.offsetWidth : void 0,
      ref: overlayRef,
      ...positionProps
    }, /* @__PURE__ */ React44.createElement(FocusScope3, {
      restoreFocus: true
    }, /* @__PURE__ */ React44.createElement(DismissButton2, {
      onDismiss: state.close
    }), /* @__PURE__ */ React44.createElement(ListBox, {
      state,
      variant,
      size,
      ...menuProps
    }), /* @__PURE__ */ React44.createElement(DismissButton2, {
      onDismiss: state.close
    }))));
  }
);
Select.Option = Item2;
Select.Section = Section;

// src/Slider/Slider.tsx
import React46, { forwardRef as forwardRef11 } from "react";
import { useSlider } from "@react-aria/slider";
import { useSliderState } from "@react-stately/slider";
import { useNumberFormatter } from "@react-aria/i18n";
import { useObjectRef as useObjectRef9 } from "@react-aria/utils";
import { useComponentStyles as useComponentStyles26 } from "@marigold/system";

// src/Slider/Thumb.tsx
import React45, { useEffect } from "react";
import { useSliderThumb } from "@react-aria/slider";
import { mergeProps as mergeProps8 } from "@react-aria/utils";
import { useStateProps as useStateProps9 } from "@marigold/system";

// src/VisuallyHidden/VisuallyHidden.tsx
import { VisuallyHidden } from "@react-aria/visually-hidden";

// src/Slider/Thumb.tsx
import { useFocusRing as useFocusRing7 } from "@react-aria/focus";
var Thumb = ({ state, trackRef, styles, ...props }) => {
  const { disabled } = props;
  const inputRef = React45.useRef(null);
  const { isFocusVisible, focusProps, isFocused } = useFocusRing7();
  const focused = isFocused || isFocusVisible || state.isThumbDragging(0);
  const stateProps = useStateProps9({
    focus: focused,
    disabled
  });
  const { thumbProps, inputProps } = useSliderThumb(
    {
      index: 0,
      trackRef,
      inputRef,
      isDisabled: disabled
    },
    state
  );
  useEffect(() => {
    state.setThumbEditable(0, !disabled);
  }, [disabled, state]);
  return /* @__PURE__ */ React45.createElement(Box, {
    __baseCSS: { top: "50%" },
    css: styles,
    ...thumbProps,
    ...stateProps
  }, /* @__PURE__ */ React45.createElement(VisuallyHidden, null, /* @__PURE__ */ React45.createElement(Box, {
    as: "input",
    type: "range",
    ref: inputRef,
    ...mergeProps8(inputProps, focusProps)
  })));
};

// src/Slider/Slider.tsx
var Slider = forwardRef11(
  ({ variant, size, width = "100%", ...props }, ref) => {
    const { formatOptions } = props;
    const trackRef = useObjectRef9(ref);
    const numberFormatter = useNumberFormatter(formatOptions);
    const state = useSliderState({ ...props, numberFormatter });
    const { groupProps, trackProps, labelProps, outputProps } = useSlider(
      {
        label: props.children,
        ...props
      },
      state,
      trackRef
    );
    const styles = useComponentStyles26(
      "Slider",
      { variant, size },
      { parts: ["track", "thumb", "label", "output"] }
    );
    return /* @__PURE__ */ React46.createElement(Box, {
      __baseCSS: {
        display: "flex",
        flexDirection: "column",
        touchAction: "none",
        width
      },
      ...groupProps
    }, /* @__PURE__ */ React46.createElement(Box, {
      __baseCSS: { display: "flex", alignSelf: "stretch" }
    }, props.children && /* @__PURE__ */ React46.createElement(Box, {
      as: "label",
      __baseCSS: styles.label,
      ...labelProps
    }, props.children), /* @__PURE__ */ React46.createElement(Box, {
      as: "output",
      ...outputProps,
      __baseCSS: { flex: "1 0 auto", textAlign: "end" },
      css: styles.output
    }, state.getThumbValueLabel(0))), /* @__PURE__ */ React46.createElement(Box, {
      ...trackProps,
      ref: trackRef,
      __baseCSS: {
        height: 32,
        width: "100%",
        cursor: props.disabled ? "not-allowed" : "pointer"
      }
    }, /* @__PURE__ */ React46.createElement(Box, {
      __baseCSS: {
        top: "50%",
        transform: "translateY(-50%)"
      },
      css: styles.track
    }), /* @__PURE__ */ React46.createElement(Thumb, {
      state,
      trackRef,
      disabled: props.disabled,
      styles: styles.thumb
    })));
  }
);

// src/Split/Split.tsx
import React47 from "react";
import { Box as Box28 } from "@marigold/system";
var Split = (props) => /* @__PURE__ */ React47.createElement(Box28, {
  ...props,
  role: "separator",
  css: { flexGrow: 1 }
});

// src/Stack/Stack.tsx
import React48 from "react";
var ALIGNMENT_X2 = {
  none: "initial",
  left: "flex-start",
  center: "center",
  right: "flex-end"
};
var ALIGNMENT_Y2 = {
  none: "initial",
  top: "flex-start",
  center: "center",
  bottom: "flex-end"
};
var Stack = ({
  children,
  space = "none",
  alignX = "none",
  alignY = "none",
  stretch = false,
  ...props
}) => /* @__PURE__ */ React48.createElement(Box, {
  css: {
    display: "flex",
    flexDirection: "column",
    p: 0,
    gap: space,
    alignItems: ALIGNMENT_X2[alignX],
    justifyContent: ALIGNMENT_Y2[alignY],
    blockSize: stretch ? "100%" : "initial"
  },
  ...props
}, children);

// src/Switch/Switch.tsx
import React49, { forwardRef as forwardRef12 } from "react";
import { useFocusRing as useFocusRing8 } from "@react-aria/focus";
import { useSwitch } from "@react-aria/switch";
import { useObjectRef as useObjectRef10 } from "@react-aria/utils";
import { useToggleState as useToggleState2 } from "@react-stately/toggle";
import {
  useComponentStyles as useComponentStyles27,
  useStateProps as useStateProps10
} from "@marigold/system";
var Switch = forwardRef12(
  ({
    variant,
    size,
    width = "100%",
    checked,
    disabled,
    readOnly,
    defaultChecked,
    ...rest
  }, ref) => {
    const inputRef = useObjectRef10(ref);
    const props = {
      isSelected: checked,
      isDisabled: disabled,
      isReadOnly: readOnly,
      defaultSelected: defaultChecked,
      ...rest
    };
    const state = useToggleState2(props);
    const { inputProps } = useSwitch(props, state, inputRef);
    const { isFocusVisible, focusProps } = useFocusRing8();
    const stateProps = useStateProps10({
      checked: state.isSelected,
      disabled,
      readOnly,
      focus: isFocusVisible
    });
    const styles = useComponentStyles27(
      "Switch",
      { variant, size },
      { parts: ["container", "label", "track", "thumb"] }
    );
    return /* @__PURE__ */ React49.createElement(Box, {
      as: "label",
      __baseCSS: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1ch",
        position: "relative",
        width
      },
      css: styles.container
    }, /* @__PURE__ */ React49.createElement(Box, {
      as: "input",
      ref: inputRef,
      css: {
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        zIndex: 1,
        opacity: 1e-4,
        cursor: inputProps.disabled ? "not-allowed" : "pointer"
      },
      ...inputProps,
      ...focusProps
    }), props.children && /* @__PURE__ */ React49.createElement(Box, {
      css: styles.label
    }, props.children), /* @__PURE__ */ React49.createElement(Box, {
      __baseCSS: {
        position: "relative",
        width: 48,
        height: 24,
        bg: "#dee2e6",
        borderRadius: 20
      },
      css: styles.track,
      ...stateProps
    }, /* @__PURE__ */ React49.createElement(Box, {
      __baseCSS: {
        display: "block",
        position: "absolute",
        top: 1,
        left: 0,
        willChange: "transform",
        transform: "translateX(1px)",
        transition: "all 0.1s cubic-bezier(.7, 0, .3, 1)",
        height: 22,
        width: 22,
        borderRadius: 9999,
        bg: "#fff",
        "&:checked": {
          transform: "translateX(calc(47px - 100%))"
        }
      },
      css: styles.thumb,
      ...stateProps
    })));
  }
);

// src/Table/Table.tsx
import React58, { useRef as useRef16 } from "react";
import { useTable } from "@react-aria/table";
import {
  Cell,
  Column,
  Row,
  TableBody as Body2,
  TableHeader as Header2,
  useTableState
} from "@react-stately/table";
import {
  Box as Box34,
  useComponentStyles as useComponentStyles28
} from "@marigold/system";

// src/Table/Context.tsx
import { createContext as createContext7, useContext as useContext7 } from "react";
var TableContext = createContext7({});
var useTableContext = () => useContext7(TableContext);

// src/Table/TableBody.tsx
import React50 from "react";
import { useTableRowGroup } from "@react-aria/table";
var TableBody = ({ children }) => {
  const { rowGroupProps } = useTableRowGroup();
  return /* @__PURE__ */ React50.createElement("tbody", {
    ...rowGroupProps
  }, children);
};

// src/Table/TableCell.tsx
import React51, { useRef as useRef10 } from "react";
import { useTableCell } from "@react-aria/table";
import { useFocusRing as useFocusRing9 } from "@react-aria/focus";
import { mergeProps as mergeProps9 } from "@react-aria/utils";
import { Box as Box29, useStateProps as useStateProps11 } from "@marigold/system";
var TableCell = ({ cell }) => {
  const ref = useRef10(null);
  const { interactive, state, styles } = useTableContext();
  const disabled = state.disabledKeys.has(cell.parentKey);
  const { gridCellProps } = useTableCell(
    {
      node: cell
    },
    state,
    ref
  );
  const cellProps = interactive ? gridCellProps : {
    ...gridCellProps,
    onMouseDown: (e) => e.stopPropagation(),
    onPointerDown: (e) => e.stopPropagation()
  };
  const { focusProps, isFocusVisible } = useFocusRing9();
  const stateProps = useStateProps11({ disabled, focusVisible: isFocusVisible });
  return /* @__PURE__ */ React51.createElement(Box29, {
    as: "td",
    ref,
    css: styles.cell,
    ...mergeProps9(cellProps, focusProps),
    ...stateProps
  }, cell.rendered);
};

// src/Table/TableCheckboxCell.tsx
import React52, { useRef as useRef11 } from "react";
import { useTableCell as useTableCell2, useTableSelectionCheckbox } from "@react-aria/table";
import { useFocusRing as useFocusRing10 } from "@react-aria/focus";
import { mergeProps as mergeProps10 } from "@react-aria/utils";
import { Box as Box30, useStateProps as useStateProps12 } from "@marigold/system";

// src/Table/utils.ts
var mapCheckboxProps = ({
  checkboxProps: {
    isIndeterminate,
    isSelected,
    isDisabled,
    defaultSelected,
    ...rest
  }
}) => {
  const checkboxProps = {
    disabled: isDisabled,
    checked: isSelected,
    defaultChecked: defaultSelected,
    indeterminate: isIndeterminate,
    ...rest
  };
  return { checkboxProps };
};

// src/Table/TableCheckboxCell.tsx
var TableCheckboxCell = ({ cell }) => {
  const ref = useRef11(null);
  const { state, styles } = useTableContext();
  const disabled = state.disabledKeys.has(cell.parentKey);
  const { gridCellProps } = useTableCell2(
    {
      node: cell
    },
    state,
    ref
  );
  const { checkboxProps } = mapCheckboxProps(
    useTableSelectionCheckbox({ key: cell.parentKey }, state)
  );
  const { focusProps, isFocusVisible } = useFocusRing10();
  const stateProps = useStateProps12({ disabled, focusVisible: isFocusVisible });
  return /* @__PURE__ */ React52.createElement(Box30, {
    as: "td",
    ref,
    __baseCSS: {
      textAlign: "center",
      verticalAlign: "middle",
      lineHeight: 1
    },
    css: styles.cell,
    ...mergeProps10(gridCellProps, focusProps),
    ...stateProps
  }, /* @__PURE__ */ React52.createElement(Checkbox, {
    ...checkboxProps
  }));
};

// src/Table/TableColumnHeader.tsx
import React53, { useRef as useRef12 } from "react";
import { useFocusRing as useFocusRing11 } from "@react-aria/focus";
import { useHover as useHover6 } from "@react-aria/interactions";
import { useTableColumnHeader } from "@react-aria/table";
import { mergeProps as mergeProps11 } from "@react-aria/utils";
import { Box as Box31, useStateProps as useStateProps13 } from "@marigold/system";
var SortIndicator = ({
  direction = "ascending",
  visible
}) => /* @__PURE__ */ React53.createElement(Box31, {
  as: "span",
  role: "presentation",
  "aria-hidden": "true",
  css: {
    color: "currentColor",
    paddingInlineStart: "0.5ch",
    visibility: visible ? "visible" : "hidden"
  }
}, direction === "ascending" ? "\u25B2" : "\u25BC");
var TableColumnHeader = ({ column }) => {
  var _a, _b;
  const ref = useRef12(null);
  const { state, styles } = useTableContext();
  const { columnHeaderProps } = useTableColumnHeader(
    {
      node: column
    },
    state,
    ref
  );
  const { hoverProps, isHovered } = useHover6({});
  const { focusProps, isFocusVisible } = useFocusRing11();
  const stateProps = useStateProps13({
    hover: isHovered,
    focusVisible: isFocusVisible
  });
  return /* @__PURE__ */ React53.createElement(Box31, {
    as: "th",
    colSpan: column.colspan,
    ref,
    __baseCSS: { cursor: "default" },
    css: styles.header,
    ...mergeProps11(columnHeaderProps, hoverProps, focusProps),
    ...stateProps
  }, column.rendered, column.props.allowsSorting && /* @__PURE__ */ React53.createElement(SortIndicator, {
    direction: (_a = state.sortDescriptor) == null ? void 0 : _a.direction,
    visible: ((_b = state.sortDescriptor) == null ? void 0 : _b.column) === column.key
  }));
};

// src/Table/TableHeader.tsx
import React54 from "react";
import { useTableRowGroup as useTableRowGroup2 } from "@react-aria/table";
var TableHeader = ({ children }) => {
  const { rowGroupProps } = useTableRowGroup2();
  return /* @__PURE__ */ React54.createElement("thead", {
    ...rowGroupProps
  }, children);
};

// src/Table/TableHeaderRow.tsx
import React55, { useRef as useRef13 } from "react";
import { useTableHeaderRow } from "@react-aria/table";
var TableHeaderRow = ({ item, children }) => {
  const { state } = useTableContext();
  const ref = useRef13(null);
  const { rowProps } = useTableHeaderRow({ node: item }, state, ref);
  return /* @__PURE__ */ React55.createElement("tr", {
    ...rowProps,
    ref
  }, children);
};

// src/Table/TableRow.tsx
import React56, { useRef as useRef14 } from "react";
import { useFocusRing as useFocusRing12 } from "@react-aria/focus";
import { useHover as useHover7 } from "@react-aria/interactions";
import { useTableRow } from "@react-aria/table";
import { mergeProps as mergeProps12 } from "@react-aria/utils";
import { Box as Box32, useStateProps as useStateProps14 } from "@marigold/system";
var TableRow = ({ children, row }) => {
  const ref = useRef14(null);
  const { interactive, state, styles } = useTableContext();
  const { rowProps, isPressed } = useTableRow(
    {
      node: row
    },
    state,
    ref
  );
  const disabled = state.disabledKeys.has(row.key);
  const selected = state.selectionManager.isSelected(row.key);
  const { focusProps, isFocusVisible } = useFocusRing12({ within: true });
  const { hoverProps, isHovered } = useHover7({
    isDisabled: disabled || !interactive
  });
  const stateProps = useStateProps14({
    disabled,
    selected,
    hover: isHovered,
    focusVisible: isFocusVisible,
    active: isPressed
  });
  return /* @__PURE__ */ React56.createElement(Box32, {
    as: "tr",
    ref,
    __baseCSS: {
      cursor: !interactive ? "text" : disabled ? "default" : "pointer"
    },
    css: styles.row,
    ...mergeProps12(rowProps, focusProps, hoverProps),
    ...stateProps
  }, children);
};

// src/Table/TableSelectAllCell.tsx
import React57, { useRef as useRef15 } from "react";
import { useFocusRing as useFocusRing13 } from "@react-aria/focus";
import { useHover as useHover8 } from "@react-aria/interactions";
import {
  useTableColumnHeader as useTableColumnHeader2,
  useTableSelectAllCheckbox
} from "@react-aria/table";
import { mergeProps as mergeProps13 } from "@react-aria/utils";
import { Box as Box33, useStateProps as useStateProps15 } from "@marigold/system";
var TableSelectAllCell = ({ column }) => {
  const ref = useRef15(null);
  const { state, styles } = useTableContext();
  const { columnHeaderProps } = useTableColumnHeader2(
    {
      node: column
    },
    state,
    ref
  );
  const { checkboxProps } = mapCheckboxProps(useTableSelectAllCheckbox(state));
  const { hoverProps, isHovered } = useHover8({});
  const { focusProps, isFocusVisible } = useFocusRing13();
  const stateProps = useStateProps15({
    hover: isHovered,
    focusVisible: isFocusVisible
  });
  return /* @__PURE__ */ React57.createElement(Box33, {
    as: "th",
    ref,
    __baseCSS: {
      textAlign: "center",
      verticalAlign: "middle",
      lineHeight: 1
    },
    css: styles.header,
    ...mergeProps13(columnHeaderProps, hoverProps, focusProps),
    ...stateProps
  }, /* @__PURE__ */ React57.createElement(Checkbox, {
    ...checkboxProps
  }));
};

// src/Table/Table.tsx
var Table = ({
  variant,
  size,
  stretch,
  selectionMode = "none",
  ...props
}) => {
  const interactive = selectionMode !== "none";
  const tableRef = useRef16(null);
  const state = useTableState({
    ...props,
    selectionMode,
    showSelectionCheckboxes: selectionMode === "multiple" && props.selectionBehavior !== "replace"
  });
  const { gridProps } = useTable(props, state, tableRef);
  const styles = useComponentStyles28(
    "Table",
    { variant, size },
    { parts: ["table", "header", "row", "cell"] }
  );
  const { collection } = state;
  return /* @__PURE__ */ React58.createElement(TableContext.Provider, {
    value: { state, interactive, styles }
  }, /* @__PURE__ */ React58.createElement(Box34, {
    as: "table",
    ref: tableRef,
    __baseCSS: {
      display: stretch ? "table" : "block",
      width: stretch ? "100%" : void 0,
      borderCollapse: "collapse",
      overflow: "auto",
      whiteSpace: "nowrap"
    },
    css: styles.table,
    ...gridProps
  }, /* @__PURE__ */ React58.createElement(TableHeader, null, collection.headerRows.map((headerRow) => /* @__PURE__ */ React58.createElement(TableHeaderRow, {
    key: headerRow.key,
    item: headerRow
  }, [...headerRow.childNodes].map(
    (column) => {
      var _a;
      return ((_a = column.props) == null ? void 0 : _a.isSelectionCell) ? /* @__PURE__ */ React58.createElement(TableSelectAllCell, {
        key: column.key,
        column
      }) : /* @__PURE__ */ React58.createElement(TableColumnHeader, {
        key: column.key,
        column
      });
    }
  )))), /* @__PURE__ */ React58.createElement(TableBody, null, [...collection.body.childNodes].map((row) => /* @__PURE__ */ React58.createElement(TableRow, {
    key: row.key,
    row
  }, [...row.childNodes].map(
    (cell) => {
      var _a;
      return ((_a = cell.props) == null ? void 0 : _a.isSelectionCell) ? /* @__PURE__ */ React58.createElement(TableCheckboxCell, {
        key: cell.key,
        cell
      }) : /* @__PURE__ */ React58.createElement(TableCell, {
        key: cell.key,
        cell
      });
    }
  ))))));
};
Table.Body = Body2;
Table.Cell = Cell;
Table.Column = Column;
Table.Header = Header2;
Table.Row = Row;

// src/Text/Text.tsx
import React59 from "react";
import {
  useComponentStyles as useComponentStyles29
} from "@marigold/system";
import { Box as Box35 } from "@marigold/system";
var Text = ({
  variant,
  size,
  align,
  color,
  fontSize,
  cursor,
  outline,
  children,
  ...props
}) => {
  const styles = useComponentStyles29("Text", {
    variant,
    size
  });
  return /* @__PURE__ */ React59.createElement(Box35, {
    as: "p",
    ...props,
    css: [styles, { color, cursor, outline, fontSize, textAlign: align }]
  }, children);
};

// src/TextArea/TextArea.tsx
import React60, { forwardRef as forwardRef13 } from "react";
import { useHover as useHover9 } from "@react-aria/interactions";
import { useFocusRing as useFocusRing14 } from "@react-aria/focus";
import { useTextField } from "@react-aria/textfield";
import { useObjectRef as useObjectRef11 } from "@react-aria/utils";
import {
  Box as Box36,
  useComponentStyles as useComponentStyles30,
  useStateProps as useStateProps16
} from "@marigold/system";
var TextArea = forwardRef13(
  ({
    variant,
    size,
    width,
    disabled,
    required,
    readOnly,
    error,
    rows,
    ...props
  }, ref) => {
    const { label, description, errorMessage } = props;
    const textAreaRef = useObjectRef11(ref);
    const { labelProps, inputProps, descriptionProps, errorMessageProps } = useTextField(
      {
        inputElementType: "textarea",
        isDisabled: disabled,
        isRequired: required,
        isReadOnly: readOnly,
        validationState: error ? "invalid" : "valid",
        ...props
      },
      textAreaRef
    );
    const { hoverProps, isHovered } = useHover9({});
    const { focusProps, isFocusVisible } = useFocusRing14();
    const stateProps = useStateProps16({
      hover: isHovered,
      focus: isFocusVisible,
      disabled,
      readOnly,
      error
    });
    const styles = useComponentStyles30("TextArea", { variant, size });
    return /* @__PURE__ */ React60.createElement(FieldBase, {
      label,
      labelProps,
      required,
      description,
      descriptionProps,
      error,
      errorMessage,
      errorMessageProps,
      stateProps,
      variant,
      size,
      width
    }, /* @__PURE__ */ React60.createElement(Box36, {
      as: "textarea",
      css: styles,
      ref: textAreaRef,
      rows,
      ...inputProps,
      ...focusProps,
      ...hoverProps,
      ...stateProps
    }));
  }
);

// src/TextField/TextField.tsx
import React61, { forwardRef as forwardRef14 } from "react";
import { useHover as useHover10 } from "@react-aria/interactions";
import { useFocusRing as useFocusRing15 } from "@react-aria/focus";
import { useTextField as useTextField2 } from "@react-aria/textfield";
import { useObjectRef as useObjectRef12 } from "@react-aria/utils";
import { useStateProps as useStateProps17 } from "@marigold/system";
var TextField = forwardRef14(
  ({ variant, size, width, disabled, required, readOnly, error, ...props }, ref) => {
    const { label, description, errorMessage, autoFocus } = props;
    const inputRef = useObjectRef12(ref);
    const { labelProps, inputProps, descriptionProps, errorMessageProps } = useTextField2(
      {
        isDisabled: disabled,
        isRequired: required,
        isReadOnly: readOnly,
        validationState: error ? "invalid" : "valid",
        ...props
      },
      inputRef
    );
    const { hoverProps, isHovered } = useHover10({});
    const { focusProps, isFocusVisible } = useFocusRing15({
      isTextInput: true,
      autoFocus
    });
    const stateProps = useStateProps17({
      hover: isHovered,
      focus: isFocusVisible,
      disabled,
      readOnly,
      error
    });
    return /* @__PURE__ */ React61.createElement(FieldBase, {
      label,
      labelProps,
      required,
      description,
      descriptionProps,
      error,
      errorMessage,
      errorMessageProps,
      stateProps,
      variant,
      size,
      width
    }, /* @__PURE__ */ React61.createElement(Input, {
      ref: inputRef,
      variant,
      size,
      ...inputProps,
      ...focusProps,
      ...hoverProps,
      ...stateProps
    }));
  }
);

// src/Tiles/Tiles.tsx
import React62 from "react";
import { useTheme as useTheme3 } from "@marigold/system";
var Tiles = ({
  space = "none",
  stretch = false,
  equalHeight = false,
  tilesWidth,
  children,
  ...props
}) => {
  const { css } = useTheme3();
  const { width } = css({ width: tilesWidth });
  let column = `min(${typeof width === "number" ? `${width}px` : width}, 100%)`;
  if (stretch) {
    column = `minmax(${column}, 1fr)`;
  }
  return /* @__PURE__ */ React62.createElement(Box, {
    ...props,
    css: {
      display: "grid",
      gap: space,
      gridTemplateColumns: `repeat(auto-fit, ${column})`,
      gridAutoRows: equalHeight ? "1fr" : void 0
    }
  }, children);
};

// src/Tooltip/Tooltip.tsx
import React64 from "react";
import { useTooltip } from "@react-aria/tooltip";
import {
  Box as Box37,
  useComponentStyles as useComponentStyles31
} from "@marigold/system";

// src/Tooltip/Context.ts
import { createContext as createContext8, useContext as useContext8 } from "react";
var TooltipContext = createContext8({});
var useTooltipContext = () => useContext8(TooltipContext);

// src/Tooltip/TooltipTrigger.tsx
import React63, { useRef as useRef17 } from "react";
import { FocusableProvider } from "@react-aria/focus";
import { useOverlayPosition as useOverlayPosition3 } from "@react-aria/overlays";
import { useTooltipTrigger } from "@react-aria/tooltip";
import { useTooltipTriggerState } from "@react-stately/tooltip";
var TooltipTrigger = ({
  disabled,
  open,
  delay = 1e3,
  placement = "top",
  children,
  ...rest
}) => {
  const [tooltipTrigger, tooltip] = React63.Children.toArray(children);
  const props = {
    ...rest,
    isDisabled: disabled,
    isOpen: open,
    delay,
    placement
  };
  const tooltipTriggerRef = useRef17(null);
  const overlayRef = useRef17(null);
  const state = useTooltipTriggerState(props);
  const { triggerProps, tooltipProps } = useTooltipTrigger(
    props,
    state,
    tooltipTriggerRef
  );
  const {
    overlayProps: positionProps,
    placement: overlayPlacement,
    arrowProps
  } = useOverlayPosition3({
    placement: props.placement,
    targetRef: tooltipTriggerRef,
    offset: props.offset,
    crossOffset: props.crossOffset,
    isOpen: state.isOpen,
    overlayRef
  });
  return /* @__PURE__ */ React63.createElement(FocusableProvider, {
    ref: tooltipTriggerRef,
    ...triggerProps
  }, tooltipTrigger, /* @__PURE__ */ React63.createElement(TooltipContext.Provider, {
    value: {
      state,
      overlayRef,
      arrowProps,
      placement: overlayPlacement,
      ...tooltipProps,
      ...positionProps
    }
  }, /* @__PURE__ */ React63.createElement(Overlay, {
    open: state.isOpen
  }, tooltip)));
};

// src/Tooltip/Tooltip.tsx
var Tooltip = ({ children, variant, size }) => {
  const { arrowProps, state, overlayRef, placement, ...rest } = useTooltipContext();
  const { tooltipProps } = useTooltip({}, state);
  const styles = useComponentStyles31(
    "Tooltip",
    { variant, size },
    { parts: ["container", "arrow"] }
  );
  return /* @__PURE__ */ React64.createElement(Box37, {
    ...tooltipProps,
    ...rest,
    ref: overlayRef,
    css: styles.container,
    "data-placement": placement
  }, /* @__PURE__ */ React64.createElement("div", null, children), /* @__PURE__ */ React64.createElement(Box37, {
    ...arrowProps,
    __baseCSS: {
      position: "absolute",
      height: 0,
      width: 0,
      borderStyle: "solid",
      borderLeftColor: "transparent",
      borderRightColor: "transparent",
      borderBottomColor: "transparent"
    },
    css: styles.arrow
  }));
};
Tooltip.Trigger = TooltipTrigger;

// src/XLoader/XLoader.tsx
import { SVG } from "@marigold/system";
import React65, { forwardRef as forwardRef15 } from "react";
var XLoader = forwardRef15((props, ref) => /* @__PURE__ */ React65.createElement(SVG, {
  id: "XLoader",
  xmlns: "http://www.w3.org/2000/svg",
  size: 150,
  viewBox: "0 0 150 150",
  ...props,
  ...ref
}, /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_1_",
  d: "M35.3 27h26.5l54 74.1H88.7z"
}), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_5_",
  d: "M124.3 12.8h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c0 2.7-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "1.0s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_18_",
  d: "M115.9 24.4h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c0 2.7-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.9s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_19_",
  d: "M107.5 35.9h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c0 2.7-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.8s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_20_",
  d: "M99.1 47.5h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c0 2.7-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.7s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_21_",
  d: "M90.7 59H90c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c0 2.8-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.6s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_22_",
  d: "M68 89.8h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.8c0 2.6-2.2 4.8-4.9 4.8z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.5s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_23_",
  d: "M59.6 101.4h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c0 2.7-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.4s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_24_",
  d: "M51.2 112.9h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c-.1 2.8-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.3s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_25_",
  d: "M42.8 124.5h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c-.1 2.7-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.2s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_26_",
  d: "M34.4 136h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c-.1 2.7-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.1s",
  dur: "2.5s",
  repeatCount: "indefinite"
})), /* @__PURE__ */ React65.createElement("path", {
  id: "XMLID_27_",
  d: "M26 147.6h-.7c-2.7 0-4.9-2.2-4.9-4.9v-.7c0-2.7 2.2-4.9 4.9-4.9h.7c2.7 0 4.9 2.2 4.9 4.9v.7c-.1 2.8-2.2 4.9-4.9 4.9z"
}, /* @__PURE__ */ React65.createElement("animate", {
  attributeName: "opacity",
  attributeType: "XML",
  values: "1; .01; 1; 1; 1;",
  begin: "0.0s",
  dur: "2.5s",
  repeatCount: "indefinite"
}))));
export {
  Aside,
  Aspect,
  Badge,
  Body,
  Box,
  Breakout,
  Button,
  Card,
  Center,
  Checkbox,
  CheckboxGroup,
  CheckboxGroupContext,
  Columns,
  Container,
  Dialog,
  Divider,
  Footer,
  Header,
  Headline,
  Image,
  Inline,
  Input,
  Label,
  Link,
  List,
  MarigoldProvider,
  Menu,
  Message,
  NumberField,
  Overlay,
  Popover,
  Radio,
  SSRProvider,
  Select,
  Slider,
  Split,
  Stack,
  Switch,
  Table,
  Text,
  TextArea,
  TextField,
  ThemeProvider2 as ThemeProvider,
  Tiles,
  Tooltip,
  Underlay,
  VisuallyHidden,
  XLoader,
  extendTheme,
  useAsyncList,
  useCheckboxGroupContext,
  useListData,
  useTheme2 as useTheme
};
